import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { MapPin, Save, Loader2, AlertTriangle, Crosshair } from "lucide-react";

export default function SchoolSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/school-settings")
      .then((res) => { setSettings(res.data); setDraft(res.data); })
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const captureLocation = () => {
    if (!navigator.geolocation) return toast.error("GPS not supported on this device");
    setCapturing(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set("school_lat", Number(pos.coords.latitude.toFixed(6)));
        set("school_lng", Number(pos.coords.longitude.toFixed(6)));
        toast.success(`Captured: ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)} (±${Math.round(pos.coords.accuracy)} m)`);
        setCapturing(false);
      },
      (err) => { toast.error(`Couldn't get location: ${err.message}`); setCapturing(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.patch("/admin/school-settings", draft);
      setSettings(data);
      setDraft(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="text-center py-10 text-[#A0AEC0] text-sm"><Loader2 className="inline animate-spin" /></div>;
  }
  if (!settings) return null;

  const locReady = draft.school_lat != null && draft.school_lng != null;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="font-display text-3xl text-[#0F1B47] font-medium">School settings</h2>
        <p className="text-sm text-[#718096]">Configure the campus location, geofence radius and attendance time-windows used by the staff QR clock-in.</p>
      </div>

      {/* Location */}
      <Card title="Campus location" icon={<MapPin size={16} />}>
        {!locReady && (
          <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-2xl px-3 py-2 text-[12px] flex gap-2 mb-3">
            <AlertTriangle size={14} className="mt-0.5 flex-none" />
            Location not set yet — staff QR clock-in won&apos;t work until you set this.
          </div>
        )}
        <button
          type="button"
          onClick={captureLocation}
          disabled={capturing}
          className="inline-flex items-center gap-2 bg-[#C8DF52] text-[#0F1B47] rounded-full px-5 py-2.5 text-sm font-bold disabled:opacity-50 mb-4"
          data-testid="school-settings-capture-location"
        >
          <Crosshair size={15} /> {capturing ? "Capturing…" : "Use my current location"}
        </button>
        <p className="text-[11px] text-[#718096] mb-3">Tip: open this page while standing at the school entrance for the most accurate pin.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude">
            <input
              type="number" step="0.000001"
              value={draft.school_lat ?? ""}
              onChange={(e) => set("school_lat", e.target.value === "" ? null : Number(e.target.value))}
              placeholder="12.9716"
              data-testid="school-settings-lat"
            />
          </Field>
          <Field label="Longitude">
            <input
              type="number" step="0.000001"
              value={draft.school_lng ?? ""}
              onChange={(e) => set("school_lng", e.target.value === "" ? null : Number(e.target.value))}
              placeholder="77.5946"
              data-testid="school-settings-lng"
            />
          </Field>
        </div>
        <Field label="School label (printed on Excel reports)">
          <input value={draft.school_label || ""} onChange={(e) => set("school_label", e.target.value)} placeholder="Naavi Early Years" data-testid="school-settings-label" />
        </Field>
      </Card>

      {/* Geofence */}
      <Card title="Geofence">
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Radius — ${draft.geofence_m || 50} m`}>
            <input type="range" min={20} max={500} step={5} value={draft.geofence_m || 50} onChange={(e) => set("geofence_m", Number(e.target.value))} data-testid="school-settings-geofence" />
          </Field>
          <Field label={`Max GPS accuracy allowed — ${draft.max_accuracy_m || 100} m`}>
            <input type="range" min={20} max={300} step={5} value={draft.max_accuracy_m || 100} onChange={(e) => set("max_accuracy_m", Number(e.target.value))} data-testid="school-settings-max-acc" />
          </Field>
        </div>
        <p className="text-[11px] text-[#718096] mt-2">
          Smaller radius = stricter geofence. Accuracy threshold rejects scans where the phone&apos;s GPS is too noisy (indoor / spoofed).
        </p>
      </Card>

      {/* Time windows */}
      <Card title="Clock-in window">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Opens at"><input type="time" value={draft.clock_in_start || ""} onChange={(e) => set("clock_in_start", e.target.value)} data-testid="school-settings-in-start" /></Field>
          <Field label="On-time cutoff"><input type="time" value={draft.on_time_cutoff || ""} onChange={(e) => set("on_time_cutoff", e.target.value)} data-testid="school-settings-on-time" /></Field>
          <Field label="Closes at"><input type="time" value={draft.clock_in_end || ""} onChange={(e) => set("clock_in_end", e.target.value)} data-testid="school-settings-in-end" /></Field>
        </div>
        <p className="text-[11px] text-[#718096] mt-2">Clock-ins after the cutoff are saved and visible to admin, but flagged as <b>Late</b>.</p>
      </Card>

      <Card title="Clock-out window">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Opens at"><input type="time" value={draft.clock_out_start || ""} onChange={(e) => set("clock_out_start", e.target.value)} data-testid="school-settings-out-start" /></Field>
          <Field label="Closes at"><input type="time" value={draft.clock_out_end || ""} onChange={(e) => set("clock_out_end", e.target.value)} data-testid="school-settings-out-end" /></Field>
        </div>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-7 py-3 font-bold text-sm shadow-xl disabled:opacity-50" data-testid="school-settings-save">
          <Save size={15} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

function Card({ title, icon, children }) {
  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-display text-xl text-[#0F1B47] font-medium">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-2">
      <span className="block text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-1">{label}</span>
      {React.cloneElement(children, { className: "w-full text-sm bg-[#F5F5F0] rounded-xl px-3 py-2 border border-[#0F1B47]/8 focus:outline-none focus:border-[#0F1B47]/30" })}
    </label>
  );
}
